import axios from "axios";
import axiosInstance from "@/services/https/global";
import CONFIG from "@/config";
import { normalizePaged } from "@/utils/normalize-paged";

const baseUrl = `${CONFIG.BASE_URL}/wayapay-notification-api/admin`;

const toError = (error) => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    const base = typeof data === "object" && data !== null ? data : { message: data };
    return { ...base, statusCode: error.response?.status };
  }
  return { message: "An unexpected error occurred" };
};

const unwrap = (res) => res.data?.data ?? res.data;

/* ---------------------------------- Message templates --------------------------------- */

export const getMessageTemplates = async ({ page = 0, size = 10 } = {}) => {
  try {
    const res = await axiosInstance.get(`${baseUrl}/message/message-templates`, {
      params: { page, size },
    });
    return normalizePaged(res.data, { page, size });
  } catch (error) {
    throw toError(error);
  }
};

export const getMessageTemplate = async (messageId) => {
  try {
    const res = await axiosInstance.get(
      `${baseUrl}/message/message-templates/${messageId}`,
    );
    return unwrap(res);
  } catch (error) {
    throw toError(error);
  }
};

// Query params, not a body — the endpoint declares message/smsEventStatus/title
// as (query). A JSON body is accepted and silently ignored.
export const createMessageTemplate = async ({ title, message, smsEventStatus }) => {
  try {
    const res = await axiosInstance.post(`${baseUrl}/message-templates`, null, {
      params: { title, message, smsEventStatus },
    });
    return unwrap(res);
  } catch (error) {
    throw toError(error);
  }
};

export const updateMessageTemplate = async ({ messageId, title, message, smsEventStatus }) => {
  try {
    const res = await axiosInstance.put(
      `${baseUrl}/message/message-templates/${messageId}`,
      null,
      { params: { title, message, smsEventStatus } },
    );
    return unwrap(res);
  } catch (error) {
    throw toError(error);
  }
};

export const toggleMessageTemplate = async (id) => {
  try {
    const res = await axiosInstance.put(`${baseUrl}/message/message-templates/toggle/${id}`);
    return unwrap(res);
  } catch (error) {
    throw toError(error);
  }
};

/* ------------------------------------ SMS gateways ------------------------------------ */

export const getSmsGateways = async () => {
  try {
    const res = await axiosInstance.get(`${baseUrl}/sms-gateway`);
    return unwrap(res) ?? [];
  } catch (error) {
    throw toError(error);
  }
};

// Reads the currently active gateway. (Swagger titles this "Activate SMS
// gateway", but it is a parameterless GET returning a single record.)
export const getActiveSmsGateway = async () => {
  try {
    const res = await axiosInstance.get(`${baseUrl}/active-sms-gateway`);
    return unwrap(res);
  } catch (error) {
    throw toError(error);
  }
};

export const createSmsGateway = async ({ name, description }) => {
  try {
    const res = await axiosInstance.post(`${baseUrl}/sms-gateway`, { name, description });
    return unwrap(res);
  } catch (error) {
    throw toError(error);
  }
};

// Update, despite the swagger summary reading "Get Single SMS gateway".
export const updateSmsGateway = async ({ id, name, description }) => {
  try {
    const res = await axiosInstance.put(`${baseUrl}/sms-gateway/${id}`, {
      id,
      name,
      description,
    });
    return unwrap(res);
  } catch (error) {
    throw toError(error);
  }
};

export const toggleSmsGateway = async (id) => {
  try {
    const res = await axiosInstance.put(`${baseUrl}/sms-gateway/toggle/${id}`);
    return unwrap(res);
  } catch (error) {
    throw toError(error);
  }
};

/* ------------------------------------- SMS charges ------------------------------------ */

export const getSmsCharges = async () => {
  try {
    const res = await axiosInstance.get(`${baseUrl}/sms-charges`);
    return unwrap(res) ?? [];
  } catch (error) {
    throw toError(error);
  }
};

export const getActiveSmsCharge = async () => {
  try {
    const res = await axiosInstance.get(`${baseUrl}/view-active-sms-charge`);
    return unwrap(res);
  } catch (error) {
    throw toError(error);
  }
};

export const createSmsCharge = async ({ fee }) => {
  try {
    const res = await axiosInstance.post(`${baseUrl}/config-sms-charges`, { fee });
    return unwrap(res);
  } catch (error) {
    throw toError(error);
  }
};

export const toggleSmsCharge = async (id) => {
  try {
    const res = await axiosInstance.put(`${baseUrl}/sms-charges/toggle/${id}`);
    return unwrap(res);
  } catch (error) {
    throw toError(error);
  }
};

/* -------------------------------------- Providers ------------------------------------- */

export const getProviders = async () => {
  try {
    const res = await axiosInstance.get(`${baseUrl}/providers`);
    return unwrap(res) ?? [];
  } catch (error) {
    throw toError(error);
  }
};

// Only the body and the {type} path segment are sent. The swagger's
// `authenticated`, `authorities`, `credentials`, `details` and `principal`
// query params are Spring's Authentication object leaking into the generated
// docs — sending them would be meaningless at best.
export const setDefaultProvider = async ({ type, provider }) => {
  try {
    const res = await axiosInstance.put(`${baseUrl}/providers/${type}/default`, { provider });
    return unwrap(res);
  } catch (error) {
    throw toError(error);
  }
};